import {
    SET_CURRENT_COMPANY_BIZMINER_RATIOS,
    SET_CURRENT_COMPANY_DUPONT_RATIOS,
    SET_CURRENT_COMPANY_ALTMAN_Z_SCORE,
} from "./mutations";
import { store } from "../../index";
import FinancialAccount from "../../../models/FinancialAccount";
import BizminerRatios from "../../../models/ratios/BizminerRatios";
import DupontRatios from "../../../models/ratios/DupontRatios";
import AltmanZScore from "../../../models/ratios/AltmanZScore";
import { fetchCompanyMonthlyBalanceSheet, fetchCompanyMonthlyProfitAndLoss } from "../financials/actions";
import { generateSDEAnalysis } from "../qualityOfEarnings/actions";
import { DESCENDANT_ORDER, MONTHLY } from "../../../config";


//--------- generate Bizminer Ratios ---------//

export const generateBizminerRatios = (options = { clearCache: false }) => async (dispatch) => {
    const { companyId, dataSettings } = store.getState().appState

    await dispatch(fetchCompanyMonthlyBalanceSheet({ companyId }, {
        periodsToCompare: dataSettings.yearsToFetch
    }))

    await dispatch(fetchCompanyMonthlyProfitAndLoss({ companyId }, {
        periodsToCompare: dataSettings.yearsToFetch
    }))

    const { currentCompanyMonthlyBalanceSheet, currentCompanyMonthlyProfitAndLoss } = store.getState().financials;
    const { currentCompanyBizminerRatios } = store.getState().financialRatios;

    //get asset accounts from balance sheet
    const [
        cash,
        marketableSecurities,
        accountsReceivable,
        inventory,
        otherCurrentAssets,
        realEstateAssets,
        otherLongTermAssets,
        grossFixedAssets,
    ] = FinancialAccount.getAssetsCategoryOptions().map((categoryObject) => {
        return currentCompanyMonthlyBalanceSheet.getYearlyAssetsCategoryValues(
        categoryObject.value,
        categoryObject.group
        ).reverse();
    });

    //get liability accounts from balance sheet
    const [
        accountsPayable,
        loansNotesPayable,
        otherCurrentLiabilities,
        longTermLiabilities,
        nonOperatingLongTermLiabilities,
        otherLongTermLiabilities,
    ] = FinancialAccount.getLiabilitiesCategoryOptions().map((categoryObject) => {
        return currentCompanyMonthlyBalanceSheet.getYearlyLiabilitiesCategoryValues(
            categoryObject.value,
            categoryObject.group
        ).reverse();
        }
    );
    
    //get total items from balancesheet
    const yearsFormatted = currentCompanyMonthlyBalanceSheet.yearsFormatted.reverse();
    const totalLongTermAssets = currentCompanyMonthlyBalanceSheet.getYearlyTotalAssets("Fixed Assets").reverse();
    const totalCurrentAssets = currentCompanyMonthlyBalanceSheet.getYearlyTotalAssets("Current Assets").reverse();
    const totalAssets = currentCompanyMonthlyBalanceSheet.getYearlyTotalAssets().reverse();
    const totalCurrentLiabilities = currentCompanyMonthlyBalanceSheet.getYearlyTotalLiabilities("Current Liabilities").reverse();
    const totalLongTermLiabilities = currentCompanyMonthlyBalanceSheet.getYearlyTotalLiabilities("Long Term Liability").reverse();
    const totalLiabilities = currentCompanyMonthlyBalanceSheet.getYearlyTotalLiabilities().reverse();
    const totalEquity = currentCompanyMonthlyBalanceSheet.getYearlyTotalEquities().reverse();

    //get items from profit and loss
    const totalSales = currentCompanyMonthlyProfitAndLoss.yearlyTotalSales.reverse();
    const laborCost = currentCompanyMonthlyProfitAndLoss.yearlyTotalLaborCost.reverse();
    const wage = currentCompanyMonthlyProfitAndLoss.yearlyTotalSalaryWage.reverse();
    const costOfSales = currentCompanyMonthlyProfitAndLoss.yearlyTotalCostOfSales.reverse();
    const yearlyGrossProfitMargin = currentCompanyMonthlyProfitAndLoss.yearlyGrossProfitMargins.reverse().map((obj) => ({
        date: obj.date,
        value: obj.value / 100,
    }));    //gross profit margin in profit and loss is percent value
    const operatingIncome = currentCompanyMonthlyProfitAndLoss.yearlyTotalOperatingIncome.reverse();
    const interestExpense = currentCompanyMonthlyProfitAndLoss.yearlyTotalInterestExpense.reverse();
    const yearlyPreTaxNetProfit = currentCompanyMonthlyProfitAndLoss.yearlyTotalPreTaxNetProfits.reverse();
    const yearlyAfterTaxNetProfit = currentCompanyMonthlyProfitAndLoss.yearlyTotalAfterTaxNetProfits.reverse();
    const EBITDA = currentCompanyMonthlyProfitAndLoss.yearlyEBITDAAsDefined.reverse();
    const yearlyOfficersCompensation = currentCompanyMonthlyProfitAndLoss.yearlyTotalOfficersCompensation.reverse();
    const yearlyDepreciationDepletionAmortization
        = currentCompanyMonthlyProfitAndLoss.yearlyTotalDepreciationDepletionAndAmortization.reverse();
    
    //generate average employee at creating or when fetching company data
    let averageEmployees;
    if (options.clearCache || !currentCompanyBizminerRatios.averageEmployees) 
        averageEmployees = yearsFormatted.map((year) => ({ date: year, value: "" }));
    else averageEmployees = currentCompanyBizminerRatios.averageEmployees;

    const _currentCompanyBizminerRatios = new BizminerRatios({
        yearsFormatted,
        cash,
        marketableSecurities,
        accountsReceivable,
        inventory,
        accountsPayable,
        loansNotesPayable,
        totalLongTermAssets,
        totalCurrentAssets,
        totalAssets,
        totalCurrentLiabilities,
        totalLongTermLiabilities,
        totalLiabilities,
        totalEquity,
        totalSales,
        costOfSales,
        laborCost,
        wage,
        yearlyGrossProfitMargin,
        operatingIncome,
        interestExpense,
        yearlyPreTaxNetProfit,
        yearlyAfterTaxNetProfit,
        EBITDA,
        yearlyDepreciationDepletionAmortization,
        yearlyOfficersCompensation,
        averageEmployees,
    })

    dispatch({ type: SET_CURRENT_COMPANY_BIZMINER_RATIOS, payload: _currentCompanyBizminerRatios })
};


//--------- update Bizminer Ratios when inputboxes are changing ---------//

export const updateBizminerRatios = (value, date) => (dispatch) => {
    const { currentCompanyBizminerRatios } = store.getState().financialRatios;

    //Modify changed yearlyAverageEmployees in store
    currentCompanyBizminerRatios.averageEmployees.filter((obj) => obj.date === date)[0].value 
        = Number(value.replace(/,/g, ""));      //input is string that includes comma

    dispatch({
      type: SET_CURRENT_COMPANY_BIZMINER_RATIOS,
      payload: new BizminerRatios({
        ...currentCompanyBizminerRatios,
      }),
    });
};


//--------- generate Dupont Ratios ---------//

export const generateDupontRatios = (options = { dateOrder: DESCENDANT_ORDER, prefix: MONTHLY }) => async (dispatch) => {
    const { companyId, dataSettings } = store.getState().appState

    await dispatch(fetchCompanyMonthlyBalanceSheet({ companyId }, {
        periodsToCompare: dataSettings.yearsToFetch
    }))

    await dispatch(fetchCompanyMonthlyProfitAndLoss({ companyId }, {
        periodsToCompare: dataSettings.yearsToFetch
    }))

    const { currentCompanyMonthlyBalanceSheet, currentCompanyMonthlyProfitAndLoss } = store.getState().financials;
    
    //get total items from balancesheet(balance sheet is date descendant order)
    const datesFormatted = currentCompanyMonthlyBalanceSheet.datesFormatted.reverse();
    const totalAssets = currentCompanyMonthlyBalanceSheet.getTotalAssets().reverse();
    const totalEquity = currentCompanyMonthlyBalanceSheet.getTotalEquities().reverse();
    const yearsFormatted = currentCompanyMonthlyBalanceSheet.yearsFormatted.reverse();
    const yearlyTotalAssets = currentCompanyMonthlyBalanceSheet.getYearlyTotalAssets().reverse();
    const yearlyTotalEquity = currentCompanyMonthlyBalanceSheet.getYearlyTotalEquities().reverse();

    //get items from profit and loss(balance sheet is date descendant order)
    const totalSales = currentCompanyMonthlyProfitAndLoss.totalSales.reverse();
    const interestExpense = currentCompanyMonthlyProfitAndLoss.totalInterestExpense.reverse();
    const EBT = currentCompanyMonthlyProfitAndLoss.totalPreTaxNetProfits.reverse();
    const netIncome = currentCompanyMonthlyProfitAndLoss.totalAfterTaxNetProfits.reverse();
    const yearlyTotalSales = currentCompanyMonthlyProfitAndLoss.yearlyTotalSales.reverse();
    const yearlyInterestExpense = currentCompanyMonthlyProfitAndLoss.yearlyTotalInterestExpense.reverse();
    const yearlyEBT = currentCompanyMonthlyProfitAndLoss.yearlyTotalPreTaxNetProfits.reverse();
    const yearlyNetIncome = currentCompanyMonthlyProfitAndLoss.yearlyTotalAfterTaxNetProfits.reverse();

    const _currentCompanyDupontRatios = new DupontRatios({
        ascendantDates: currentCompanyMonthlyBalanceSheet.ascendantDates,
        datesFormatted,
        yearsFormatted,
        totalAssets,
        totalEquity,
        totalSales,
        interestExpense,
        EBT,
        netIncome,
        yearlyTotalAssets,
        yearlyTotalEquity,
        yearlyTotalSales,
        yearlyInterestExpense,
        yearlyEBT,
        yearlyNetIncome,
        dateOrder: options.dateOrder,
        prefix: options.prefix,
    })

    dispatch({ type: SET_CURRENT_COMPANY_DUPONT_RATIOS, payload: _currentCompanyDupontRatios })
};


//--------- generate Altman Z Score ---------//

export const generateAltmanZScore
    = (options = { dateOrder: DESCENDANT_ORDER, prefix: MONTHLY, businessType: "PRIVATE_MANUFACTURING" }) => async (dispatch) => {
        const { companyId, dataSettings } = store.getState().appState

        await dispatch(fetchCompanyMonthlyBalanceSheet({ companyId }, {
            periodsToCompare: dataSettings.yearsToFetch
        }))

        await dispatch(fetchCompanyMonthlyProfitAndLoss({ companyId }, {
            periodsToCompare: dataSettings.yearsToFetch
        }))

        const { currentCompanyMonthlyBalanceSheet, currentCompanyMonthlyProfitAndLoss } = store.getState().financials;
        const { currentCompanyAltmanZScore } = store.getState().financialRatios;

        //get total items from balancesheet(balance sheet is date descendant order)
        const datesFormatted = currentCompanyMonthlyBalanceSheet.datesFormatted.reverse();
        const yearsFormatted = currentCompanyMonthlyBalanceSheet.yearsFormatted.reverse();
        const monthlyCurrentAssets = currentCompanyMonthlyBalanceSheet.getTotalAssets("Current Assets").reverse();
        const monthlyTotalAssets = currentCompanyMonthlyBalanceSheet.getTotalAssets().reverse();
        const monthlyCurrentLiabilities = currentCompanyMonthlyBalanceSheet.getTotalLiabilities("Current Liabilities").reverse();
        const monthlyTotalLiabilities = currentCompanyMonthlyBalanceSheet.getTotalLiabilities().reverse();
        const monthlyBookValueOfEquity = currentCompanyMonthlyBalanceSheet.getTotalEquities().reverse();
        const monthlyRawRetainedEarnings =
            currentCompanyMonthlyBalanceSheet.getEquitiesCategoryValues(
                FinancialAccount.getAllowedEquitiesCategories().RETAINED_EARNINGS_CATEGORY.value
            ).reverse();
        const monthlyNetIncome =
            currentCompanyMonthlyBalanceSheet.getEquitiesCategoryValues(
                FinancialAccount.getAllowedEquitiesCategories().NET_INCOME_CATEGORY.value
            ).reverse();
        const yearlyCurrentAssets = currentCompanyMonthlyBalanceSheet.getYearlyTotalAssets("Current Assets").reverse();
        const yearlyTotalAssets = currentCompanyMonthlyBalanceSheet.getYearlyTotalAssets().reverse();
        const yearlyCurrentLiabilities = currentCompanyMonthlyBalanceSheet.getYearlyTotalLiabilities("Current Liabilities").reverse();
        const yearlyTotalLiabilities = currentCompanyMonthlyBalanceSheet.getYearlyTotalLiabilities().reverse();
        const yearlyBookValueOfEquity = currentCompanyMonthlyBalanceSheet.getYearlyTotalEquities().reverse();
        const yearlyRawRetainedEarnings =
            currentCompanyMonthlyBalanceSheet.getYearlyEquitiesCategoryValues(
                FinancialAccount.getAllowedEquitiesCategories().RETAINED_EARNINGS_CATEGORY.value
            ).reverse();
        const yearlyNetIncome =
            currentCompanyMonthlyBalanceSheet.getYearlyEquitiesCategoryValues(
                FinancialAccount.getAllowedEquitiesCategories().NET_INCOME_CATEGORY.value
            ).reverse();

        //get items from profit and loss(balance sheet is date descendant order)
        const monthlyRevenue = currentCompanyMonthlyProfitAndLoss.totalSales.reverse();
        const monthlyEBIT = currentCompanyMonthlyProfitAndLoss.earningsBeforeIncomeTaxes.reverse();
        const yearlyRevenue = currentCompanyMonthlyProfitAndLoss.yearlyTotalSales.reverse();
        const yearlyEBIT = currentCompanyMonthlyProfitAndLoss.yearlyEarningsBeforeIncomeTaxes.reverse();

        const _currentCompanyAltmanZScore = new AltmanZScore({
            ascendantDates: currentCompanyMonthlyBalanceSheet.ascendantDates,
            datesFormatted,
            yearsFormatted,
            monthlyCurrentAssets,
            monthlyTotalAssets,
            monthlyCurrentLiabilities,
            monthlyTotalLiabilities,
            monthlyBookValueOfEquity,
            monthlyRawRetainedEarnings,
            monthlyNetIncome,
            monthlyRevenue,
            monthlyEBIT,
            yearlyCurrentAssets,
            yearlyTotalAssets,
            yearlyCurrentLiabilities,
            yearlyTotalLiabilities,
            yearlyBookValueOfEquity,
            yearlyRawRetainedEarnings,
            yearlyNetIncome,
            yearlyRevenue,
            yearlyEBIT,
            dateOrder: options.dateOrder,
            prefix: options.prefix,
            businessType: currentCompanyAltmanZScore?.businessType ? currentCompanyAltmanZScore.businessType : options.businessType,
    })

    dispatch({ type: SET_CURRENT_COMPANY_ALTMAN_Z_SCORE, payload: _currentCompanyAltmanZScore })
};


//--------- update dateOrder of Ratios when dateOrder selection is changing ---------//

export const updateRatiosDateOrder = (dateOrder) => async (dispatch) => {
    const { currentCompanyDupontRatios, currentCompanyAltmanZScore } = store.getState().financialRatios;

    const _currentCompanyDupontRatios = new DupontRatios({
        ...currentCompanyDupontRatios,
        dateOrder,
    });

    const _currentCompanyAltmanZScore = new AltmanZScore({
        ...currentCompanyAltmanZScore,
        dateOrder,
    });

    dispatch({ type: SET_CURRENT_COMPANY_DUPONT_RATIOS, payload: _currentCompanyDupontRatios });
    dispatch({ type: SET_CURRENT_COMPANY_ALTMAN_Z_SCORE, payload: _currentCompanyAltmanZScore });
};


//--------- update prefix(monthly/yearly) of Ratios when period selection is changing ---------//

export const updateRatiosPeriod = (prefix) => async (dispatch) => {
    const { currentCompanyDupontRatios, currentCompanyAltmanZScore } = store.getState().financialRatios;

    const _currentCompanyDupontRatios = new DupontRatios({
        ...currentCompanyDupontRatios,
        prefix,
    });

    const _currentCompanyAltmanZScore = new AltmanZScore({
        ...currentCompanyAltmanZScore,
        prefix,
    });

    dispatch({ type: SET_CURRENT_COMPANY_DUPONT_RATIOS, payload: _currentCompanyDupontRatios });
    dispatch({ type: SET_CURRENT_COMPANY_ALTMAN_Z_SCORE, payload: _currentCompanyAltmanZScore });
};


//--------- update businessType of Altman Z Score when businessType selection is changing ---------//

export const updateBusinessType = (businessType) => async (dispatch) => {
    const { currentCompanyAltmanZScore } = store.getState().financialRatios;

    const _currentCompanyAltmanZScore = new AltmanZScore({
        ...currentCompanyAltmanZScore,
        businessType,
    });

    dispatch({ type: SET_CURRENT_COMPANY_ALTMAN_Z_SCORE, payload: _currentCompanyAltmanZScore });
};